import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment-timezone';
import { apiClient } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { formatDuration } from '../../utils/monitoringFormat';

const DISPLAY_TZ = 'America/Chicago';

export type RemoteSupportSessionRow = {
  id: string;
  dateLabel: string;
  computerName: string;
  employeeName: string;
  operatorName: string;
  startLabel: string;
  endLabel: string;
  durationSeconds: number | null;
  outcome: string;
  clipboardOps: number;
  chatMessages: number;
  fileTransfers: number;
  supportActions: number;
  endReason: string;
  status: string;
  raw: Record<string, unknown>;
};

function pick(row: Record<string, any>, ...keys: string[]): any {
  for (const key of keys) {
    if (row[key] != null && row[key] !== '') return row[key];
  }
  return null;
}

function formatClock(value?: string | Date | null): string {
  if (!value) return '—';
  return moment(value).tz(DISPLAY_TZ).format('h:mm A');
}

function formatDate(value?: string | Date | null): string {
  if (!value) return '—';
  return moment(value).tz(DISPLAY_TZ).format('YYYY-MM-DD');
}

function computeDurationSeconds(row: Record<string, any>): number | null {
  const explicit = pick(row, 'duration_seconds', 'durationSeconds');
  if (explicit != null && Number.isFinite(Number(explicit))) return Math.max(0, Number(explicit));
  const started = pick(row, 'started_at', 'startedAt');
  const ended = pick(row, 'ended_at', 'endedAt');
  if (!started) return null;
  const startMs = moment(started).valueOf();
  const endMs = ended ? moment(ended).valueOf() : Date.now();
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs < startMs) return null;
  return Math.round((endMs - startMs) / 1000);
}

export function normalizeRemoteSupportSession(row: Record<string, any>): RemoteSupportSessionRow {
  const started = pick(row, 'started_at', 'startedAt');
  const ended = pick(row, 'ended_at', 'endedAt');
  const computerName = pick(
    row,
    'computer_name',
    'computerName',
    'device_name',
    'deviceName',
  ) || pick(row, 'device_uid', 'deviceUid') || '—';
  const employeeName = pick(
    row,
    'employee_name',
    'employeeName',
  ) || (row.employee
    ? `${row.employee.firstName || row.employee.first_name || ''} ${row.employee.lastName || row.employee.last_name || ''}`.trim()
    : null) || '—';
  const operatorName = pick(
    row,
    'operator_name',
    'operatorName',
    'started_by_name',
    'startedByName',
  ) || (row.operator
    ? `${row.operator.firstName || row.operator.first_name || ''} ${row.operator.lastName || row.operator.last_name || ''}`.trim()
    : null) || pick(row, 'started_by_user_id', 'startedByUserId') || '—';

  return {
    id: String(pick(row, 'id', 'session_id', 'sessionId') || ''),
    dateLabel: formatDate(started),
    computerName: String(computerName),
    employeeName: String(employeeName || '—'),
    operatorName: String(operatorName || '—'),
    startLabel: formatClock(started),
    endLabel: formatClock(ended),
    durationSeconds: computeDurationSeconds(row),
    outcome: String(pick(row, 'outcome', 'status') || '—'),
    clipboardOps: Number(pick(row, 'clipboard_ops_count', 'clipboardOpsCount', 'clipboard_used') || 0) || 0,
    chatMessages: Number(pick(row, 'chat_message_count', 'chatMessageCount') || 0) || 0,
    fileTransfers: Number(pick(row, 'file_transfer_count', 'fileTransferCount') || 0) || 0,
    supportActions: Number(pick(row, 'support_action_count', 'supportActionCount', 'actions_count') || 0) || 0,
    endReason: String(pick(row, 'end_reason', 'endReason') || '—'),
    status: String(pick(row, 'status') || ''),
    raw: row,
  };
}

function extractSessionList(payload: any): Record<string, any>[] {
  const data = payload?.data?.data ?? payload?.data ?? payload;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.sessions)) return data.sessions;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.items)) return data.items;
  return [];
}

function downloadCsv(rows: RemoteSupportSessionRow[], filename: string) {
  const headers = [
    'Date',
    'Computer',
    'Employee',
    'Operator',
    'Start',
    'End',
    'DurationSeconds',
    'Outcome',
    'Clipboard',
    'Chat',
    'Files',
    'Actions',
    'EndReason',
  ];
  const lines = [
    headers.join(','),
    ...rows.map((row) => ([
      row.dateLabel,
      row.computerName,
      row.employeeName,
      row.operatorName,
      row.startLabel,
      row.endLabel,
      row.durationSeconds ?? '',
      row.outcome,
      row.clipboardOps,
      row.chatMessages,
      row.fileTransfers,
      row.supportActions,
      row.endReason,
    ].map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))),
  ];
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

const TABLE_COLUMNS = [
  'Date',
  'Computer',
  'Employee',
  'Operator',
  'Start',
  'End',
  'Duration',
  'Outcome',
  'Clipboard',
  'Chat',
  'Files',
  'Actions',
  'End Reason',
] as const;

export interface RemoteSupportSessionsTabProps {
  /** When set, scopes history to one device (Computer Detail). */
  deviceId?: string;
  /** Hide computer column when scoped to a single device. */
  hideComputerColumn?: boolean;
}

const RemoteSupportSessionsTab: React.FC<RemoteSupportSessionsTabProps> = ({
  deviceId,
  hideComputerColumn = Boolean(deviceId),
}) => {
  const { user } = useAuth();
  const permissions = user?.permissions || [];
  const canRead = permissions.includes('monitoring.remote_control.read')
    || permissions.includes('monitoring.remote_control.start');

  const [fromDate, setFromDate] = useState(moment().subtract(7, 'days').format('YYYY-MM-DD'));
  const [toDate, setToDate] = useState(moment().format('YYYY-MM-DD'));
  const [search, setSearch] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const listParams = useMemo(() => {
    const params: Record<string, string | number | undefined> = {
      from: fromDate || undefined,
      to: toDate || undefined,
      limit: 200,
    };
    if (appliedSearch.trim()) {
      params.search = appliedSearch.trim();
      params.q = appliedSearch.trim();
    }
    if (deviceId) params.deviceId = deviceId;
    return params;
  }, [fromDate, toDate, appliedSearch, deviceId]);

  const { data, isLoading, isFetching, refetch, isError, error } = useQuery({
    queryKey: ['remoteControlSessions', deviceId || 'all', listParams],
    queryFn: () => (
      deviceId
        ? apiClient.monitoring.listDeviceRemoteControlSessions(deviceId, listParams)
        : apiClient.monitoring.listRemoteControlSessions(listParams)
    ),
    enabled: canRead,
  });

  const { data: detailData, isLoading: detailLoading } = useQuery({
    queryKey: ['remoteControlSession', selectedId],
    queryFn: () => apiClient.monitoring.getRemoteControlSession(selectedId!),
    enabled: canRead && Boolean(selectedId),
  });

  const rows = useMemo(
    () => extractSessionList(data).map((row) => normalizeRemoteSupportSession(row)),
    [data],
  );

  const visibleColumns = hideComputerColumn
    ? TABLE_COLUMNS.filter((col) => col !== 'Computer')
    : [...TABLE_COLUMNS];

  const detail = detailData?.data?.data
    ? normalizeRemoteSupportSession(detailData.data.data)
    : null;

  if (!canRead) {
    return (
      <Alert severity="warning">
        Remote Support session history requires the monitoring.remote_control.read permission.
      </Alert>
    );
  }

  return (
    <Box data-testid="remote-support-sessions-tab">
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Past remote support sessions. Counts only — clipboard contents and keystrokes are never shown.
      </Typography>

      <Box display="flex" gap={2} mb={2} flexWrap="wrap" alignItems="center">
        <TextField
          size="small"
          label="From"
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ 'data-testid': 'remote-support-from' }}
        />
        <TextField
          size="small"
          label="To"
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ 'data-testid': 'remote-support-to' }}
        />
        {!deviceId && (
          <TextField
            size="small"
            label="Search computer / operator"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') setAppliedSearch(search);
            }}
            sx={{ minWidth: 240 }}
            inputProps={{ 'data-testid': 'remote-support-search' }}
          />
        )}
        <Button
          variant="outlined"
          onClick={() => {
            setAppliedSearch(search);
            void refetch();
          }}
          disabled={isFetching}
        >
          Refresh
        </Button>
        <Button
          variant="outlined"
          onClick={() => downloadCsv(
            rows,
            `remote-support-sessions-${fromDate}-to-${toDate}.csv`,
          )}
          disabled={rows.length === 0}
          data-testid="remote-support-export-csv"
        >
          Export CSV
        </Button>
      </Box>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error as any)?.response?.data?.message || 'Failed to load remote support sessions.'}
        </Alert>
      )}

      {isLoading ? (
        <CircularProgress size={24} data-testid="remote-support-loading" />
      ) : (
        <TableContainer component={Paper} data-testid="remote-support-table">
          <Table size="small">
            <TableHead>
              <TableRow>
                {visibleColumns.map((label) => (
                  <TableCell key={label}>{label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ py: 2 }}
                      data-testid="remote-support-empty"
                    >
                      No remote support sessions for this range.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : rows.map((row) => (
                <TableRow
                  key={row.id || `${row.dateLabel}-${row.startLabel}-${row.operatorName}`}
                  hover
                  sx={{ cursor: row.id ? 'pointer' : 'default' }}
                  onClick={() => row.id && setSelectedId(row.id)}
                >
                  <TableCell>{row.dateLabel}</TableCell>
                  {!hideComputerColumn && <TableCell>{row.computerName}</TableCell>}
                  <TableCell>{row.employeeName}</TableCell>
                  <TableCell>{row.operatorName}</TableCell>
                  <TableCell>{row.startLabel}</TableCell>
                  <TableCell>{row.endLabel}</TableCell>
                  <TableCell>{formatDuration(row.durationSeconds)}</TableCell>
                  <TableCell>{row.outcome}</TableCell>
                  <TableCell>{row.clipboardOps}</TableCell>
                  <TableCell>{row.chatMessages}</TableCell>
                  <TableCell>{row.fileTransfers}</TableCell>
                  <TableCell>{row.supportActions}</TableCell>
                  <TableCell>{row.endReason}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={Boolean(selectedId)} onClose={() => setSelectedId(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Remote Support Session</DialogTitle>
        <DialogContent dividers>
          {detailLoading || !detail ? (
            <CircularProgress size={24} />
          ) : (
            <Box display="flex" flexDirection="column" gap={0.75}>
              <Typography variant="body2"><strong>Computer:</strong> {detail.computerName}</Typography>
              <Typography variant="body2"><strong>Employee:</strong> {detail.employeeName}</Typography>
              <Typography variant="body2"><strong>Operator:</strong> {detail.operatorName}</Typography>
              <Typography variant="body2">
                <strong>Window:</strong> {detail.dateLabel} {detail.startLabel} – {detail.endLabel}
                {' '}({formatDuration(detail.durationSeconds)})
              </Typography>
              <Typography variant="body2"><strong>Outcome:</strong> {detail.outcome}</Typography>
              <Typography variant="body2"><strong>End reason:</strong> {detail.endReason}</Typography>
              <Typography variant="body2">
                <strong>Counts:</strong> Clipboard {detail.clipboardOps} · Chat {detail.chatMessages}
                {' '}· Files {detail.fileTransfers} · Actions {detail.supportActions}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Clipboard text and keystrokes are not stored or displayed.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSelectedId(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RemoteSupportSessionsTab;
